import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TripPhoto } from '../../types';
import { tripPhotoUrl } from '../../utils/tripPhotos';
import Spinner from '../ui/Spinner';
import { Check, Icon, Upload } from '../ui/Icon';

interface Props {
  /** Photos the author can choose from: the day's, plus any just uploaded. */
  photos: TripPhoto[];
  initialBody?: string;
  initialPhotoIds?: string[];
  initialShared?: boolean;
  onSubmit: (body: string, photoIds: string[], shared: boolean) => Promise<void>;
  onCancel: () => void;
  /** Uploads to the gallery and returns what was stored, to pre-select it. */
  onUpload: (files: File[]) => Promise<TripPhoto[]>;
}

export default function JournalEntryForm({
  photos, initialBody = '', initialPhotoIds = [], initialShared = true, onSubmit, onCancel, onUpload,
}: Props) {
  const { t } = useTranslation();
  const [body, setBody] = useState(initialBody);
  const [selected, setSelected] = useState<string[]>(initialPhotoIds);
  const [shared, setShared] = useState(initialShared);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function toggle(id: string) {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    setUploading(true);
    try {
      const stored = await onUpload(Array.from(list));
      setSelected(prev => [...prev, ...stored.map(p => p.id)]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleSubmit() {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await onSubmit(body.trim(), selected, shared);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 space-y-3">
      <textarea
        value={body}
        onChange={e => setBody(e.target.value)}
        placeholder={t('journal.placeholder')}
        rows={5}
        autoFocus
        className="input w-full resize-y text-sm leading-relaxed"
      />

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
            {t('journal.choosePhotos', { count: selected.length })}
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-ghost text-xs gap-1"
          >
            {uploading ? <Spinner size="sm" /> : <Icon as={Upload} size={12} />}
            {t('journal.addPhotos')}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={e => handleFiles(e.target.files)} />
        </div>
        {photos.length === 0 ? (
          <p className="text-xs text-slate-400">{t('journal.noPhotosToChoose')}</p>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
            {photos.map(photo => {
              const on = selected.includes(photo.id);
              return (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => toggle(photo.id)}
                  aria-pressed={on}
                  aria-label={photo.originalName}
                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer ring-2 transition ${
                    on ? 'ring-teal-500' : 'ring-transparent opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={tripPhotoUrl(photo.thumbnailFilename ?? photo.filename)} alt="" className="w-full h-full object-cover" />
                  {on && (
                    <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center">
                      <Icon as={Check} size={12} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ADR-0044: each author decides; the owner only switches the section on. */}
      <label className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          checked={shared}
          onChange={e => setShared(e.target.checked)}
          className="accent-teal-600 mt-0.5 shrink-0"
        />
        <span>
          {t('journal.sharedLabel')}
          <span className="block text-2xs text-slate-400">{t('journal.sharedHint')}</span>
        </span>
      </label>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary text-sm">{t('common.cancel')}</button>
        <button type="button" onClick={handleSubmit} disabled={saving || !body.trim()} className="btn-primary text-sm disabled:opacity-60">
          {saving ? <Spinner size="sm" /> : t('common.save')}
        </button>
      </div>
    </div>
  );
}
